import { getFriendlySampleSubtypeOrTypeLabel } from '@resistapp/client/utils/sample-subtype-filter-utils';
import { qaLabelDefinitions } from '@resistapp/common/qa-label-utils';
import { QaLabelType } from '@resistapp/common/types';
import { Cell } from './chip-heatmap';

export interface TooltipData extends Cell {}

export function ChipHeatmapTooltipContent({ d }: { d: TooltipData }) {
  const baseStyle: React.CSSProperties = { fontFamily: 'monospace' };
  const sampleSubtypeOrTypeLabel = getFriendlySampleSubtypeOrTypeLabel(d.sampleType, d.sampleSubtype);

  // Create individual styles for each metric so we can colour and strike individually.
  const ctStyle: React.CSSProperties = { ...baseStyle };
  const tmStyle: React.CSSProperties = { ...baseStyle };
  const effStyle: React.CSSProperties = { ...baseStyle };

  if (d.qaResultLabelType != null && d.qaResultLabelType !== QaLabelType.PRODUCT) {
    ctStyle.color = 'red';
    tmStyle.color = 'red';
    effStyle.color = 'red';
    ctStyle.textDecoration = 'line-through';
    tmStyle.textDecoration = 'line-through';
    effStyle.textDecoration = 'line-through';
  } else if (typeof d.rejectRule === 'number') {
    // Apply rule-specific colour and strikethrough
    switch (d.rejectRule) {
      case 0: // Tm rule violation
        tmStyle.color = 'red';
        tmStyle.textDecoration = 'line-through';
        break;
      case 1: // Ct rule - red
        ctStyle.color = 'red';
        ctStyle.textDecoration = 'line-through';
        break;
      case 2: // Efficiency rule - red
        effStyle.color = 'red';
        effStyle.textDecoration = 'line-through';
        break;
      case 3: // Ct rule - orange
        ctStyle.color = 'orange';
        ctStyle.textDecoration = 'line-through';
        break;
      case 4: // Efficiency rule - orange
        effStyle.color = 'orange';
        effStyle.textDecoration = 'line-through';
        break;
      default:
        // No disqualification - keep default style
        break;
    }
  }

  const fmt = (val: number | null) => (val == null || !Number.isFinite(val) ? '--.--' : val.toFixed(2));
  const qaLabelRows = d.qaLabels.map(label => {
    const definition = qaLabelDefinitions[label.labelType];
    const tmText = label.tm == null ? '' : ` @ ${label.tm.toFixed(1)}`;
    const resultText = label.appliesToResults ? (label.effectiveForResults ? 'results' : 'overridden') : 'training';
    const noteText = label.note.trim().length > 0 ? ` | ${label.note}` : '';
    return `${definition.title}${tmText} [${resultText}]${noteText}`;
  });

  return (
    <div style={{ padding: 8 }}>
      <table style={{ width: '100%' }}>
        <tbody>
          <tr>
            <td>Sample:</td>
            <td>{d.sampleName ? `${d.sample} - ${d.sampleName}` : d.sample}</td>
          </tr>
          <tr>
            <td>Assay:</td>
            <td>{d.assay}</td>
          </tr>
          {sampleSubtypeOrTypeLabel ? (
            <tr>
              <td>Type:</td>
              <td>{sampleSubtypeOrTypeLabel}</td>
            </tr>
          ) : null}
          <tr>
            <td>Row,Col:</td>
            <td>
              {d.row},{d.column}
            </td>
          </tr>
          <tr>
            <td>Cq:</td>
            <td>
              <code style={ctStyle}>{fmt(d.ct)}</code>
            </td>
          </tr>
          <tr>
            <td>Tm:</td>
            <td>
              <code style={tmStyle}>{fmt(d.tm)}</code>
            </td>
          </tr>
          <tr>
            <td>Efficiency:</td>
            <td>
              <code style={effStyle}>{fmt(d.efficiency)}</code>
            </td>
          </tr>
          <tr>
            <td>Flags:</td>
            <td>{d.flags}</td>
          </tr>
          <tr>
            <td>Status:</td>
            <td>
              {d.qaResultLabelType != null
                ? d.qaResultLabelType === QaLabelType.PRODUCT
                  ? `QA Product (${d.qaResultLabelScope})`
                  : `QA ${qaLabelDefinitions[d.qaResultLabelType].title} (${d.qaResultLabelScope})`
                : d.rejectRule == null
                  ? '✓ Ok'
                  : `× Disqualified (rule ${d.rejectRule})`}
            </td>
          </tr>
          {qaLabelRows.length > 0 && (
            <tr>
              <td>QA labels:</td>
              <td>{qaLabelRows.join(' | ')}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
